import {DrawReference} from '@platform/ui/DrawReference';
import {drawNumber} from '@platform/contracts/drawNumber';
import {useEffect,useState,type ReactNode} from 'react';
import type {Card,Snapshot} from '@platform/contracts/x';
import {Dialog,DialogHeading} from '@platform/ui/Dialog';
import {FormSelect} from '@platform/ui/FormSelect';
import {SteppedNumberInput} from '@platform/ui/SteppedNumberInput';
import {useCopy,useUi} from '@platform/ui/config';
import {ActionButton} from './ActionButton';
import {RoomNotice} from './RoomNotice';
import './QuickSelectionDialog.css';

export function QuickSelectionDialog({state,disabled,mode,onMode,favorite,preferencesReady,remainingNewCards,onSelect,onClose,inlinePurchase=false,resultNotice}:{
 state:Snapshot;disabled:boolean;mode:string;onMode:(mode:string)=>void;favorite:(card:Card)=>boolean;preferencesReady:boolean;remainingNewCards:number;onSelect:(ids:string[])=>void;onClose:()=>void;inlinePurchase?:boolean;resultNotice?:ReactNode;
}){
 const t=useCopy(),ui=useUi();
 // Draft eligibility uses the minimum top-up. Purchase validates actual funds.
 // The target draw is authoritative; catalogue tabs, colours and search are display-only.
 const candidates=state.cards.filter(c=>c.ownMinor+state.tombala.limits.minimumAmountMinor<=state.tombala.limits.maximumCardAmountMinor
  &&(mode!=='favorites'||favorite(c))&&(mode!=='unplayed'||c.ownMinor===0));
 const ownedCount=candidates.filter(c=>c.ownMinor>0).length;
 const maximum=Math.min(state.tombala.limits.maximumCardsPerCommand,ownedCount+Math.min(remainingNewCards,candidates.length-ownedCount));
 const [count,setCount]=useState(()=>String(Math.min(ui.controls.quickSelectionDefaultCount,maximum)));
 const pending=mode==='favorites'&&!preferencesReady;
 useEffect(()=>{
  setCount(previous=>String(Math.min(maximum,/^[1-9]\d*$/.test(previous)?Number(previous):ui.controls.quickSelectionDefaultCount)));
 },[maximum,mode,ui.controls.quickSelectionDefaultCount]);
 const number=/^[1-9]\d*$/.test(count)?Number(count):0;
 const valid=!pending&&Number.isSafeInteger(number)&&number>=1&&number<=maximum;
 const noticeKey=disabled?'auto.unavailable':pending?'auto.preferencesPending':!maximum?'auto.empty':!valid?'auto.range':'auto.available';
 const apply=()=>{
  if(disabled||!valid)return;
  // Suggestions only; draw randomness belongs to the independent RNG service.
  const shuffled=candidates.map(card=>({card,order:crypto.getRandomValues(new Uint32Array(1))[0]})).sort((a,b)=>a.order-b.order).map(x=>x.card);
  let slots=remainingNewCards;
  onSelect(shuffled.filter(c=>{if(c.ownMinor>0)return true;if(!slots)return false;slots--;return true;}).slice(0,number).map(c=>c.id));
 };
 return <Dialog open className="quick-selection-dialog purchase-dialog" title={t('auto.title')} heading={<DialogHeading title={t('auto.title')} titleDetail={<span className="quick-selection-draw" aria-label={t('room.selectionDraw',{draw:drawNumber(state)})}><DrawReference draw={drawNumber(state)}/></span>} onClose={onClose}/>} onClose={onClose}><form onSubmit={e=>{e.preventDefault();apply();}}>
  <p className="dialog-intro">{t(inlinePurchase?'auto.liveHelp':'auto.help')}</p>
  {resultNotice}
  <div className="auto-fields">
   <label>{t('auto.mode')}<FormSelect label={t('auto.mode')} disabled={disabled} value={mode} onChange={onMode} options={['random','favorites','unplayed'].map(value=>({value,label:t('auto.'+value)}))}/></label>
   <SteppedNumberInput label={t('auto.count')} text={count} value={number} minimum={maximum?1:0} maximum={maximum} onChange={setCount} disabled={disabled||pending||!maximum} invalid={!valid} describedBy="auto-count-status" unit={t('auto.unit')} layout="stacked"/>
  </div>
  <RoomNotice id="auto-count-status" noticeKey={noticeKey}>{t(noticeKey,{max:maximum})}</RoomNotice>
  <div className="room-dialog-actions"><ActionButton type="submit" className="full-width" disabled={disabled||!valid}>{t('auto.apply')}</ActionButton></div>
 </form></Dialog>;
}
